import React, { useMemo } from "react";

import { HvGrid, HvButton } from "@hitachivantara/uikit-react-core";
import { Delete } from "@hitachivantara/uikit-react-icons";

import { QueryRule } from "../types";
import Context from "../Context";

import { wrapWithTooltip } from "./utils";
import AttributeDropdown from "./AttributeDropdown";
import OperatorDropdown from "./OperatorDropdown";
import ValueInput from "./ValueInput";
import { useRuleStyles } from "./styles";

interface RuleProps extends QueryRule {
  id: number;
  disabled: boolean;
  combinator: string;
  isInvalid: boolean;
}

const Rule = ({
  id,
  combinator,
  attribute,
  operator,
  value,
  disabled,
  isInvalid,
}: RuleProps): JSX.Element => {
  const classes = useRuleStyles();
  const context = React.useContext(Context);

  const { askAction, attributes, operators, labels } = context;

  const availableOperators = useMemo(() => {
    const attributeSpec = attribute != null ? attributes[attribute] : null;
    if (attributeSpec != null) {
      const typeOperators = operators[attributeSpec.type];
      if (typeOperators != null) {
        return typeOperators.reduce(
          (count, item) =>
            count + (item.combinators.includes(combinator) ? 1 : 0),
          0
        );
      }
    }

    return -1;
  }, [attribute, attributes, combinator, operators]);

  const shouldShowValueInput =
    operator !== "Empty" && operator !== "is_not_empty";

  return (
    <HvGrid container className={classes.root} spacing={0} wrap="nowrap">
      <HvGrid item xs={2} lg={3}>
        <AttributeDropdown
          attribute={attribute}
          combinator={combinator}
          id={id}
          disabled={disabled}
          isInvalid={isInvalid}
        />
      </HvGrid>
      {attribute != null && availableOperators > 0 && (
        <HvGrid item xs={2} lg={3}>
          <OperatorDropdown
            key={id + combinator + attribute + operator}
            id={id}
            combinator={combinator}
            attribute={attribute}
            operator={operator}
          />
        </HvGrid>
      )}
      {attribute != null && (operator != null || availableOperators === 0) && (
        <HvGrid item xs>
          {shouldShowValueInput && (
            <ValueInput
              key={id + combinator + attribute + operator}
              attribute={attribute}
              id={id}
              operator={operator}
              value={value}
            />
          )}
        </HvGrid>
      )}
      <HvGrid item className={classes.actionsContainer}>
        <HvButton
          icon
          onClick={() => {
            askAction({
              actions: [{ type: "remove-node", id }],
              dialog: labels.rule.delete,
            });
          }}
        >
          {wrapWithTooltip(labels.rule.delete.tooltip, <Delete />, true)}
        </HvButton>
      </HvGrid>
    </HvGrid>
  );
};

export default Rule;
